<template>
    <div
        class="primary-editorial-box"
        data-qa="primary-editorial-box"
    >
        <div class="box-wrapper">
            <span
                v-if="data.preTitle"
                class="pre-title"
            >
                {{ data.preTitle }}
            </span>

            <h4
                v-if="data.title"
                class="title"
            >
                {{ data.title }}
            </h4>

            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="data.text"
                v-html="localizeHrefs(data.text)"
            ></div>
            <!-- eslint enable -->

            <ContentButton
                v-if="data.button"
                :data="data.button"
                page-type="product-listing"
            />
        </div>

        <div
            v-if="data.image"
            class="box-image"
        >
            <CmsLink
                v-if="data.button && data.button.link"
                :link="data.button.link"
            >
                <CmsImage
                    :data="data.image"
                    fit="cover"
                />
            </CmsLink>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { Colors } from '@/components/ui'
import { cmsColorToButtonColor } from '@/components/cms'
import { getColorForBackground } from '@/components/ui/buttons'
import type { Banner2Fragment } from '@/graphql/api'

import ContentButton from '@/components/cms/component/content/ContentButton.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import CmsLink from '@/components/cms/CmsLink.vue'
import { useHrefLocalizer } from '@/router/href-localizer'

const props = defineProps<{
    data: Banner2Fragment
}>()

const localizeHrefs = useHrefLocalizer()

const backgroundColor = computed((): Colors => {
    return cmsColorToButtonColor(props.data.backgroundColor)
})

const textColor = computed((): Colors => {
    return getColorForBackground(
        cmsColorToButtonColor(props.data.backgroundColor)
    )
})
</script>

<style lang="scss" scoped>
.primary-editorial-box {
    width: 100%;
    background: v-bind(backgroundColor);
    color: v-bind(textColor);
    position: relative;
    grid-row: 18 / 18;
    display: flex;
    flex-wrap: wrap;
    align-content: center;
    margin: 0 0 20px;

    @include min-width(640) {
        grid-row: 4/4;
        flex-wrap: nowrap;
        grid-column: 1 / span 2;
        margin: 0;
    }

    @include min-width(768) {
        grid-column: 2 / span 2;
    }

    @include min-width(1024) {
        grid-column: 3 / span 2;
    }

    @include hover() {
        .box-image {
            @include min-width(1024) {
                padding: 20px;
            }
        }

        .cms-image {
            border-radius: 170px;
        }
    }

    .box-image {
        position: relative;
        z-index: 2;
        flex-shrink: 0;
        width: 100%;
        height: auto;
        line-height: 0;
        aspect-ratio: 3/4;
        transition: padding 0.5s ease-out;
        padding-left: 10px;

        @include min-width(640) {
            aspect-ratio: initial;
            width: 50%;
        }

        @include min-width(768) {
            height: 100%;
        }

        .cms-image {
            transition: all 0.3s ease-out;
        }
    }

    .box-wrapper {
        width: 100%;
        position: relative;
        z-index: 2;
        padding: 40px 20px 20px;
        display: flex;
        align-items: center;
        align-content: center;
        justify-content: center;
        text-align: center;
        flex-wrap: wrap;
        aspect-ratio: 3/4;

        @include min-width(640) {
            aspect-ratio: initial;
        }
    }

    .pre-title {
        font-family: $effra-font-stack;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        font-weight: 400;
        display: block;
        margin: 0 0 10px;
    }

    .title {
        font-family: $parcel-font-stack;
        font-size: 1.5rem;
        line-height: 1em;
        width: 100%;
        text-align: center;
        margin: 0 0 20px;
    }

    p {
        width: 100%;
        text-align: center;
    }
}
</style>
